import { useEffect, useMemo, type CSSProperties, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import type { RailItem, RailUtilitySlots } from '@clarix/shared-types';
import { cn } from '@clarix/ui';
import { SidebarProvider, useSidebar } from './context/SidebarContext';
import { TopBar } from './TopBar';
import { SidebarRail } from './SidebarRail';
import { SidebarPanel, type PanelTitles } from './SidebarPanel';
import { SearchPaletteShell } from './SearchPaletteShell';
import { NotificationBellShell } from './NotificationBellShell';
import { BASE_RAIL_TOP } from './constants/base-rail-items';

export interface AppShellProps {
  /** Items contributed by the active app (e.g. APP_REGISTRY entry's railItems). */
  railItems?: readonly RailItem[];
  /** Items merged into the "More" popover. */
  moreItems?: readonly RailItem[];
  /** Tracker bottom-button click; hidden if not provided. */
  onOpenTrackerModal?: () => void;
  /** App switcher node, rendered at the bottom of the SidebarRail. The shell
   *  composes it from APP_REGISTRY + the user's app access; the layout
   *  package just renders the slot. */
  appSwitcher?: ReactNode;
  /** When provided, replaces <Outlet/>. Used by standalone mode which
   *  composes its own <Routes>. */
  children?: ReactNode;
  /**
   * Renders the body of the hover/pinned sidebar panel for a given panelId.
   * The layout owns the panel chrome (positioning, header, arrow, scroll);
   * the shell supplies per-section content (project tree, calendar links, …)
   * using the shared service packages. Return null for unknown panels.
   */
  renderPanelContent?: (panelId: string, onNavigate: () => void) => ReactNode;
  /**
   * Hide the SidebarRail (and its panels) and let the content span the full
   * width. Used for routes that render as standalone full-page experiences
   * (AppDefinition.railHiddenRoutes). The TopBar stays.
   */
  hideRail?: boolean;
  /**
   * Hide the TopBar as well, so the app owns the ENTIRE viewport — no rail, no
   * header. Used for immersive full-screen flows (AppDefinition.chromeHiddenRoutes),
   * e.g. a first-run setup wizard. Callers pass `hideRail` alongside this.
   */
  hideTopBar?: boolean;
  /** Optional per-item navigation-target resolver, forwarded to SidebarRail
   *  (e.g. routing the Projects item to a non-admin's first project). */
  resolveHref?: (item: RailItem) => string | undefined;
  /**
   * Whether `railItems` is authoritative for the current app. False while the
   * app's RBAC matrix is still hydrating and gated items are fail-closed
   * (hidden), so the rail is transiently missing items it will soon show.
   *
   * The "panel doesn't belong to this app → close & forget it" effect below
   * keys off rail membership; it MUST wait for this to be true, otherwise a
   * restored panel gets force-forgotten during the hydration window (its
   * owning rail item is temporarily absent) and never reopens — the pinned
   * secondary sidebar collapsing on every hard refresh. Defaults to `true`
   * for standalone/dev harnesses that render the full rail immediately.
   */
  railReady?: boolean;
  /**
   * The active app's backend is down: render its rail present-but-inert instead
   * of letting it collapse. Gated rail items fail closed until the app's RBAC
   * matrix hydrates from that app's own api, so during an outage the matrix
   * never arrives and the sidebar would shrink to the ungated entries — looking
   * like a permissions problem rather than an outage.
   *
   * Disables the nav only; the app switcher / Tracker / user menu stay live so
   * the user can leave. The pinned secondary panel is also suppressed — its
   * contents are fetched from the same dead api.
   */
  railDisabled?: boolean;
  /**
   * panelId → header text, from the active app's object-driven sidebar config.
   * Wins over the layout's legacy title map, so an app names its own panels.
   */
  panelTitles?: PanelTitles;
  /**
   * The active app's rail utility slots (`AppDefinition.sidebar.utility`).
   * Omitted fields render, so an app opts OUT of a slot rather than in.
   */
  utility?: RailUtilitySlots;
  /** The active app's id, scoping the pinned panel's persisted state so it never leaks between apps. */
  appId?: string;
  /** Hold the rail's nav empty while the active app's permissions resolve. */
  railLoading?: boolean;
}

/**
 * Top-level chrome — TopBar + SidebarRail + content area. Exact-port of
 * clario-frontend/AppShell layout structure, with stubbed slots for
 * features that haven't been ported yet (see design doc).
 *
 * Apps must NOT import AppShell directly. The shell owns the layout;
 * apps contribute icons by exporting `railItems` from their register.ts.
 */
export function AppShell(props: AppShellProps) {
  return (
    <SidebarProvider scope={props.appId}>
      <AppShellInner {...props} />
    </SidebarProvider>
  );
}

function AppShellInner({
  railItems = [],
  moreItems = [],
  onOpenTrackerModal,
  appSwitcher,
  children,
  renderPanelContent,
  hideRail = false,
  hideTopBar = false,
  resolveHref,
  railReady = true,
  railDisabled = false,
  panelTitles,
  utility,
  railLoading = false,
}: AppShellProps) {
  const { isPanelOpen, activePanel, closePanel, panelWidth, isResizing } = useSidebar();

  const items = useMemo(() => [...BASE_RAIL_TOP, ...railItems], [railItems]);

  // The pinned panel id persists in localStorage, which is shared across
  // apps on this origin. If the open panel doesn't belong to the current
  // app's rail (e.g. Projects' tree was pinned, then Admin is opened), close
  // and forget it so one app's sidebar never leaks into another.
  //
  // Guarded by `railReady`: while an app's RBAC matrix is still hydrating its
  // gated items are fail-closed (absent), so a legitimately-restored panel
  // would look "foreign" and get force-forgotten — collapsing the pinned
  // sidebar on every refresh. Wait until the rail is authoritative.
  useEffect(() => {
    if (!railReady) return;
    if (!activePanel) return;
    if (!items.some((it) => it.panelId === activePanel)) {
      closePanel({ forget: true });
    }
  }, [railReady, activePanel, items, closePanel]);

  // `railDisabled` suppresses the pinned panel, so its gutter collapses too.
  const showPanelGutter = !hideRail && isPanelOpen && !railDisabled;
  const railWidth = hideRail ? 0 : 64;
  // Runtime width, so exposed as CSS vars; content and in-app fixed bars
  // (BulkActionBar) offset by `--sidebar-offset`.
  const chromeVars = {
    '--sidebar-panel-w': `${panelWidth}px`,
    '--sidebar-offset': `${railWidth + (showPanelGutter ? panelWidth : 0)}px`,
  } as CSSProperties;

  return (
    <div
      className={cn(
        'bg-background relative flex h-screen min-h-screen flex-col',
        isResizing && 'cursor-col-resize select-none',
      )}
      style={chromeVars}
    >
      {/* The profile/avatar moved out of the TopBar — it now lives at the
          bottom of the SidebarRail and opens the platform profile page. */}
      {!hideTopBar && (
        <TopBar
          search={<SearchPaletteShell />}
          notifications={<NotificationBellShell />}
          panelPinned={showPanelGutter}
          panelToggle={!hideRail && !railDisabled}
        />
      )}
      {!hideRail && (
        <>
          <SidebarRail
            items={items}
            moreItems={moreItems}
            onOpenTrackerModal={onOpenTrackerModal}
            appSwitcher={appSwitcher}
            renderPanelContent={renderPanelContent}
            resolveHref={resolveHref}
            disabled={railDisabled}
            panelTitles={panelTitles}
            utility={utility}
            loading={railLoading}
          />
          {/* Pinned (click-opened) panel. The floating hover panel is rendered by
              SidebarRail anchored to the hovered icon. Suppressed during an app
              outage: its body (project tree, ticket counts, …) comes from the
              same unreachable api, so it would only render empty scaffolding. */}
          {!railDisabled && (
            <SidebarPanel
              items={items}
              renderPanelContent={renderPanelContent}
              panelTitles={panelTitles}
            />
          )}
        </>
      )}

      <div
        className={cn(
          'bg-background flex min-h-0 flex-1 flex-col',
          // Follow the drag handle 1:1 instead of easing behind it.
          !isResizing && 'transition-all duration-300 ease-in-out',
          // The TopBar is fixed at top-0 h-12, so content is normally offset by
          // pt-12. When it's hidden the app owns the full height — drop the offset.
          !hideTopBar && 'pt-12',
          !hideRail && 'lg:ml-[var(--sidebar-offset)]',
        )}
      >
        {/* No key={location.pathname} here: re-keying main on every navigation
            remounts the <Outlet> subtree — including the lazy-loaded remote in
            AppRoute — which makes React.lazy re-suspend and flashes the
            AppBootSplash (logo + spinner) on each page switch. Keeping it
            mounted lets the remote persist so only the destination page's own
            skeleton shows during data loads. */}
        {/* `pb-0`: full-height pages fill to the bottom edge so a table's sticky
            pagination footer sits flush at the bottom with no blank strip below
            it. Top spacing under the TopBar is kept via `pt-4`. */}
        {/* `pt-4` gives content breathing room under the TopBar; a chromeless
            full-screen flow owns the top edge (its own background must reach it). */}
        <main className={cn('bg-surface flex min-h-0 flex-1 flex-col', !hideTopBar && 'pt-4')}>
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}
